const $ = window.jQuery || window.$;

import { Controller } from "@hotwired/stimulus"

import tinymce from 'tinymce';
import 'tinymce/models/dom';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';

import 'tinymce/plugins/advlist';
import 'tinymce/plugins/code';
import 'tinymce/plugins/link';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/table';
import 'tinymce/plugins/image';

export default class extends Controller {
  static targets = ['input', 'template', 'counterBox', 'subjectBox', 'subject']
  static values = { plaintextOption: { type: String, default: 'sms' } }

  _initTiny() {
    const hideMenubar = this.inputTarget.dataset.hideMenubar === "true";
    const hideStatusbar = this.inputTarget.dataset.hideStatusbar === "true";
    const readonly = this.inputTarget.dataset.readonly === "true";

    tinymce.init({
      target: this.inputTarget,
      plugins: this.plaintext ? '' : 'advlist code link lists table image',
      toolbar: this.buildToolbar(readonly),
      paste_as_text: this.plaintext,
      newline_behavior: this.plaintext ? 'linebreak' : 'default',
      link_assume_external_targets: 'https',
      link_default_protocol: 'https',
      convert_urls: false,
      menubar: !hideMenubar && !readonly && !this.plaintext,
      statusbar: !hideStatusbar,
      readonly: readonly,
      setup: (editor) => this.setup(editor),
      file_picker_types: 'image',
      file_picker_callback: this.filePickerCallback,
      branding: false,
      promotion: false,
      content_css: this.inputTarget.dataset.css ? [this.inputTarget.dataset.css] : [],
      content_style: this.inputTarget.dataset.contentStyle,
      skin_url: '/assets/tinymce/skins/ui/oxide',
    });
  }

  buildToolbar(readonly) {
    if (readonly) return false;
    if (this.plaintext) return this.variablesEnabled ? 'variables' : false;

    let toolbar = 'bold italic link bullist numlist';

    if (!this.attachmentsDisabled) toolbar += ' attachments';
    if (this.variablesEnabled) toolbar += ' variables';

    return toolbar;
  }

  connect() {
    this._isPreview = false;
    this._originalHtml = null;
    this.attachmentsDisabled = this.inputTarget.dataset.disableAttachments === "true";
    this.attachmentsTargetSelector = this.inputTarget.dataset.attachmentsTarget || '#attachments-field';
    this.headerStyle = this.inputTarget.dataset.headerStyle || null;
    this.tinymceStyle = this.inputTarget.dataset.tinymceStyle || null;
    this.variables = JSON.parse(this.inputTarget.dataset.variables || '[]');
    this.variablesEnabled = !!this.inputTarget.dataset.enabledVariables;
    this.plaintext = this.inputTarget.dataset.plaintext === "true";
    this.variablesTooltip = this.inputTarget.dataset.variablesTooltip;

    this._onExternalPreview = () => {
      const editor = window.tinymce.activeEditor;
      if (!editor) return;
      this.togglePreview(editor);
    };
    document.addEventListener('tinymce:togglePreview', this._onExternalPreview);

    this.attachmentsHandler = (e) => {
      const files = e.target.files;
      const maxSizeBytes = Number(this.inputTarget.dataset.max_size);
      const validExtensions = this.inputTarget.dataset.extensions
        .split(',')
        .map(ext => ext.trim().toLowerCase());

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const extension = file.name.split('.').pop().toLowerCase();

        if (!validExtensions.includes(extension) || file.size > maxSizeBytes) {
          tinymce.activeEditor?.notificationManager.open({
            text: $("#new_email .error").data("error-message"),
            type: 'error',
            timeout: 3000
          });
          e.target.value = '';
          return false;
        }
      }
    };

    if (!this.inputTarget.id) {
      this.inputTarget.id = `tinymce_${Math.random().toString(36).slice(2)}`;
    }
  
    const prev = tinymce.get(this.inputTarget.id);
    if (prev) prev.remove();
  
    this._initTiny();

    $(document).on('change', '#attachments-field', this.attachmentsHandler);
  }

  disconnect () {
    try { $(document).off('change', '#attachments-field', this.attachmentsHandler); } catch(e) {}

    document.removeEventListener('tinymce:togglePreview', this._onExternalPreview);

    const ed = this.editor || tinymce.get(this.inputTarget.id);
    if (ed) { ed.remove(); }
    this.editor = null;
  }

  setup(editor) {
    editor.on('init', () => {
      this.editor = editor;
      this.setErrorTarget(editor);
      this.applyCustomStyles();
    });

    editor.on('input', this.sync.bind(this, editor));
    editor.on('change', this.sync.bind(this, editor));
    editor.on('setcontent', this.sync.bind(this, editor));
    editor.on('keyup', this.sync.bind(this, editor));
  
    this.registerAttachments(editor);
    this.registerVariables(editor);
  }

  sync(editor) {
    editor.save();
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }));
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }));
    this.syncValidationState();
  }

  syncValidationState() {
    const validator = window.materialValidator;
    if (!validator || typeof validator.showError !== "function") return;

    const editorContainer = this.editor?.getContainer();
    const hasValidationError =
      this.inputTarget.classList.contains("is-invalid") ||
      !!editorContainer?.classList.contains("is-invalid");

    if (!hasValidationError) return;

    validator.showError(this.inputTarget);
  }

  registerVariables(editor) {
    editor.ui.registry.addIcon('variables', '<i class="material-icons fs-22">code</i>');
  
    if (!this.variablesEnabled) return;
  
    editor.ui.registry.addMenuButton('variables', this.variablesMenuButtonConfig(editor));
  }
  
  variablesMenuButtonConfig(editor) {
    const tooltip = this.variablesTooltip;
  
    return {
      icon: 'variables',
      tooltip,
  
      onSetup: () => this.markVariablesButtonKeepEnabled(editor, tooltip),
  
      fetch: (callback) => {
        callback(
          (this.variables || []).map((v) => ({
            type: 'menuitem',
            text: v.value,
            onAction: () => this.insertVariable(editor, v)
          }))
        );
      }
    };
  }
  
  markVariablesButtonKeepEnabled(editor, tooltip) {
    requestAnimationFrame(() => {
      const container = editor.getContainer();
      if (!container) return;
  
      const sel = `.tox-tbtn[title="${tooltip}"]`;
      const btn = container.querySelector(sel);
  
      if (btn) btn.dataset.keepEnabled = "true";
    });
  }
  
  insertVariable(editor, v) {
    if (this.plaintext) return editor.insertContent(this.escapeHtml(`{{${v.label}}}`));

    const variableValue = this.escapeHtml(v.value);
    const variableName = this.escapeHtml(v.label);
    const tagHtml = `<span class="text-xs smart-variable" contenteditable="false" data-mce-contenteditable="false" data-variable="${variableName}">${variableValue}</span>`;
    editor.insertContent(tagHtml);
  }

  escapeHtml(value) {
    const div = document.createElement("div");
    div.textContent = (value || "").toString();
    return div.innerHTML;
  }

  registerAttachments(editor) {
    editor.ui.registry.addIcon('attachments', '<i class="material-icons fs-22">attach_file</i>');
  
    editor.ui.registry.addButton('attachments', {
      icon: 'attachments',
      onAction: () => {
        if (!this.attachmentsTargetSelector) return;
  
        this.inputTarget.dispatchEvent(new CustomEvent("attachments:open", {
          detail: { selector: this.attachmentsTargetSelector }
        }));
      }
    });
  }

  setErrorTarget(editor) {
    const container = editor.getContainer();
    container.id = `${this.inputTarget.id}_tinymce`;
    const targetSelector = `#${container.id}`;
    this.inputTarget.dataset.errorTarget = targetSelector;
    $(this.inputTarget).data('error-target', targetSelector);
  }

  applyCustomStyles() {
    const rules = this.buildStyleRules();
    if (!rules.length) return;

    const container = this.editor?.getContainer();
    if (!container) return;

    rules.forEach((rule) => {
      const style = rule.style;
      if (!style) return;

      const selectors = rule.selectors || [];
      if (!selectors.length) {
        container.style.cssText += `; ${style}`;
        return;
      }

      selectors.forEach((selector) => {
        if (!selector) return;
        if (selector === ":container") {
          container.style.cssText += `; ${style}`;
          return;
        }

        container.querySelectorAll(selector).forEach((node) => {
          node.style.cssText += `; ${style}`;
        });
      });
    });
  }

  buildStyleRules() {
    const rules = [];

    if (this.headerStyle) {
      rules.push({
        selectors: [".tox-editor-header", ".tox-toolbar__primary"],
        style: this.headerStyle
      });
    }

    if (this.tinymceStyle) {
      rules.push({ selectors: [":container"], style: this.tinymceStyle });
    }

    return rules;
  }

  async togglePreview(editor) {
    if (!this._isPreview) {
      await this.enterPreview(editor);
    } else {
      this.exitPreview(editor);
    }
  }

  async enterPreview(editor) {
    this._originalHtml = editor.getContent({ format: 'html' });

    const previewUrl = this.inputTarget.dataset.previewUrl;
    let resolvedHtml = null;

    if (previewUrl) {
      try {
        const payload = {
          html: this._originalHtml,
          email_thread_id: this.inputTarget.dataset.emailThreadId,
          recipient_id: this.inputTarget.dataset.previewRecipientId
        };

        const resp = await $.ajax({
          url: previewUrl,
          type: 'POST',
          data: JSON.stringify(payload),
          contentType: 'application/json; charset=UTF-8',
          dataType: 'json'
        });
  
        resolvedHtml = resp.html ?? this._originalHtml;
      } catch (err) {
        resolvedHtml = this._buildFrontendPreview(this._originalHtml);
      }
    } else {
      resolvedHtml = this._buildFrontendPreview(this._originalHtml);
    }
  
    editor.undoManager.transact(() => {
      editor.setContent(resolvedHtml, { format: 'html' });
    });
  
    editor.mode.set('readonly');
    this._isPreview = true;
  }

  exitPreview(editor) {
    editor.undoManager.transact(() => {
      editor.setContent(this._originalHtml, { format: 'html' });
    });

    editor.mode.set('design');

    this._isPreview = false;
  }

  buildFrontendPreview(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    doc.querySelectorAll('.smart-variable').forEach(node => {
      node.querySelectorAll('.smart-variable-remove').forEach(x => x.remove());

      const textValue = node.textContent.toString().trim();

      const strong = doc.createElement('strong');
      strong.className = 'preview-var';
      strong.textContent = textValue;

      node.replaceWith(strong);
    });

    return doc.body.innerHTML;
  }

  filePickerCallback (cb, value, meta) {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');

    input.onchange = function () {
      const file = this.files[0];
      const reader = new FileReader();
      reader.onload = function () {
        const id = 'blobid' + (new Date()).getTime();
        const blobCache =  tinymce.activeEditor.editorUpload.blobCache;
        const base64 = reader.result.split(',')[1];
        const blobInfo = blobCache.create(id, file, base64);
        blobCache.add(blobInfo);
        cb(blobInfo.blobUri(), { title: file.name });
      }
      reader.readAsDataURL(file);
    }

    input.click();
  }

  switchMode(event) {
    this.plaintext = event.target.value === this.plaintextOptionValue

    const editor = this.editor || tinymce.get(this.inputTarget.id)
    if (editor) editor.remove()

    this.inputTarget.value = ''
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this._initTiny()
    this.toggleCounter()
    this.toggleSubject()
  }

  toggleCounter() {
    if (!this.hasCounterBoxTarget) return

    this.counterBoxTarget.classList.toggle('tw-hidden', !this.plaintext)
  }

  toggleSubject() {
    if (!this.hasSubjectTarget) return

    this.subjectBoxTarget.classList.toggle('tw-hidden', this.plaintext)
    this.subjectTarget.required = !this.plaintext
    if (this.plaintext) this.subjectTarget.value = ''
  }

  updateContent(event) {
    const select = event.target
    const editor = this.editor || tinymce.get(this.inputTarget.id)
    if (!editor) return

    const option = select?.selectedOptions?.[0]
    const content = option.dataset?.content

    editor.setContent(this.plaintext ? this.escapeHtml(content).replace(/\r?\n/g, '<br>') : content)
    this.sync(editor)
  }
}
